import * as Cesium from 'cesium';
// Distance threshold for considering two points as connected (in meters)
export const CONNECTION_THRESHOLD_METERS = 5;
export const DEFAULT_HIGHLIGHT_COLOR = '#00BFFF'; // Deep sky blue
// Default base line width when the user hasn't picked one. Matches the
// slider's default position in StreamHighlightDialog. Camera-aware
// scaling (getHighlightWidth) multiplies this for zoom.
export const DEFAULT_HIGHLIGHT_LINE_WIDTH = 3;
export const computeCoordHash = (entity) => {
    var _a;
    if (!entity.polyline)
        return '';
    const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
    if (!positions || positions.length === 0)
        return '';
    const first = positions[0];
    const mid = positions[Math.floor(positions.length / 2)];
    const last = positions[positions.length - 1];
    const coords = [first, mid, last].map(p => {
        const c = Cesium.Cartographic.fromCartesian(p);
        return `${c.longitude.toFixed(6)},${c.latitude.toFixed(6)}`;
    }).join('|');
    let hash = 0;
    for (let i = 0; i < coords.length; i++) {
        hash = ((hash << 5) - hash) + coords.charCodeAt(i);
        hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
};
export const getHighlightWidth = (cameraHeight, baseWidth) => {
    if (cameraHeight < 500)
        return Math.max(baseWidth * 1.5, 3);
    if (cameraHeight < 2000)
        return Math.max(baseWidth * 2, 4);
    if (cameraHeight < 10000)
        return Math.max(baseWidth * 2.5, 6);
    return Math.max(baseWidth * 3, 8);
};
export const isStreamArtifact = (artifact) => {
    var _a, _b, _c;
    try {
        const name = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getName) === 'function'
            ? artifact.metadata.getName()
            : '';
        const lowerName = name.toLowerCase();
        const streamFriendlyNames = ['micro', 'minor', 'intermediate', 'major'];
        if (streamFriendlyNames.includes(lowerName)) {
            const metadataJson = typeof ((_b = artifact.metadata) === null || _b === void 0 ? void 0 : _b.getMetadata) === 'function'
                ? artifact.metadata.getMetadata()
                : '';
            if (metadataJson) {
                try {
                    const meta = JSON.parse(metadataJson);
                    const objectKey = (meta.object_key || '').toLowerCase();
                    if (objectKey.includes('stream')) {
                        return true;
                    }
                }
                catch (_d) {
                    // JSON parse failed
                }
            }
        }
        const metadataJson = typeof ((_c = artifact.metadata) === null || _c === void 0 ? void 0 : _c.getMetadata) === 'function'
            ? artifact.metadata.getMetadata()
            : '';
        if (metadataJson) {
            try {
                const meta = JSON.parse(metadataJson);
                const objectKey = (meta.object_key || '').toLowerCase();
                if (objectKey.includes('stream') || objectKey.includes('drainage')) {
                    return true;
                }
            }
            catch (_e) {
                // JSON parse failed
            }
        }
        return lowerName.includes('stream') ||
            lowerName.includes('hydro') ||
            lowerName.includes('drainage');
    }
    catch (_f) {
        return false;
    }
};
export const extractStreamThreshold = (artifact) => {
    var _a, _b;
    try {
        const metadataJson = typeof ((_a = artifact.metadata) === null || _a === void 0 ? void 0 : _a.getMetadata) === 'function'
            ? artifact.metadata.getMetadata()
            : '';
        if (metadataJson) {
            try {
                const meta = JSON.parse(metadataJson);
                const objectKey = meta.object_key || '';
                const thresholdMatch = objectKey.match(/streams?[_-]?(\d+)/i);
                if (thresholdMatch) {
                    return thresholdMatch[1];
                }
            }
            catch (_c) {
                // JSON parse failed
            }
        }
        const name = typeof ((_b = artifact.metadata) === null || _b === void 0 ? void 0 : _b.getName) === 'function'
            ? artifact.metadata.getName()
            : 'Unknown';
        const friendlyNameToThreshold = {
            'micro': '250',
            'minor': '500',
            'intermediate': '2500',
            'major': '5000'
        };
        const lowerName = name.toLowerCase();
        if (friendlyNameToThreshold[lowerName]) {
            return friendlyNameToThreshold[lowerName];
        }
        return name;
    }
    catch (_d) {
        return 'Unknown';
    }
};
export const extractLineCoordinates = (entity) => {
    var _a;
    if (!entity.polyline)
        return [];
    const positions = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
    if (!positions || positions.length === 0)
        return [];
    return positions.map((pos) => {
        const cartographic = Cesium.Cartographic.fromCartesian(pos);
        return {
            lat: Cesium.Math.toDegrees(cartographic.latitude),
            lng: Cesium.Math.toDegrees(cartographic.longitude)
        };
    });
};
// Haversine distance between two coordinates in meters
export const haversineDistance = (c1, c2) => {
    const R = 6371000;
    const lat1Rad = c1.lat * Math.PI / 180;
    const lat2Rad = c2.lat * Math.PI / 180;
    const deltaLat = (c2.lat - c1.lat) * Math.PI / 180;
    const deltaLng = (c2.lng - c1.lng) * Math.PI / 180;
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};
// Get the start and end points of a polyline
export const getEndpoints = (coords) => {
    if (coords.length < 2)
        return null;
    return {
        start: coords[0],
        end: coords[coords.length - 1]
    };
};
// Check if two streams are connected (share an endpoint)
export const areStreamsConnected = (coords1, coords2) => {
    const ep1 = getEndpoints(coords1);
    const ep2 = getEndpoints(coords2);
    if (!ep1 || !ep2)
        return false;
    return (haversineDistance(ep1.start, ep2.start) < CONNECTION_THRESHOLD_METERS ||
        haversineDistance(ep1.start, ep2.end) < CONNECTION_THRESHOLD_METERS ||
        haversineDistance(ep1.end, ep2.start) < CONNECTION_THRESHOLD_METERS ||
        haversineDistance(ep1.end, ep2.end) < CONNECTION_THRESHOLD_METERS);
};
